import { DocsService } from '@affine/core/modules/doc';
import { TagService } from '@affine/core/modules/tag';
import { TagsIcon } from '@blocksuite/affine-components/icons';
import { updateBlockType } from '@blocksuite/affine/blocks/note';
import {
  focusTextModel,
  getInlineEditorByModel,
  onModelTextUpdated,
} from '@blocksuite/affine/rich-text';
import { NotificationProvider } from '@blocksuite/affine-shared/services';
import {
  SlashMenuConfigExtension,
  type SlashMenuActionContext,
  type SlashMenuActionItem,
  type SlashMenuContext,
} from '@blocksuite/affine/widgets/slash-menu';
import { NowIcon } from '@blocksuite/icons/lit';
import type { ExtensionType } from '@blocksuite/store';
import type { FrameworkProvider } from '@toeverything/infra';
import { html } from 'lit';

function normalizeTagName(name: string) {
  return name.trim().toLocaleLowerCase();
}

function getTagName(actionContext?: SlashMenuActionContext) {
  return actionContext?.argument.trim() ?? '';
}

function tagSwatchIcon(color: string) {
  return html`<div
    style="width:14px;height:14px;border-radius:50%;background:${color ||
    'var(--affine-tag-gray)'};"
  ></div>`;
}

export function patchTagSlashMenu(framework: FrameworkProvider): ExtensionType {
  const tagService = framework.get(TagService);
  const docsService = framework.get(DocsService);

  const findTagByName = (name: string) => {
    const normalizedName = normalizeTagName(name);
    return tagService.tagList.tags$.value.find(
      tag => normalizeTagName(tag.value$.value) === normalizedName
    );
  };

  const toast = (ctx: SlashMenuContext, message: string) => {
    ctx.std.getOptional(NotificationProvider)?.toast(message);
  };

  const applyTag = (
    ctx: SlashMenuContext,
    actionContext?: SlashMenuActionContext
  ) => {
    const tagName = getTagName(actionContext);
    if (!tagName) {
      toast(ctx, 'Type a tag name after /tag');
      return;
    }

    const pageId = ctx.std.store.id;
    const docRecord = docsService.list.doc$(pageId).value;
    if (!docRecord) {
      toast(ctx, 'Cannot tag this page');
      return;
    }

    const existingTag = findTagByName(tagName);
    const tag =
      existingTag ??
      tagService.tagList.createTag(tagName, tagService.randomTagColor());
    const tagIds = docRecord.meta$.value.tags ?? [];
    if (tagIds.includes(tag.id)) {
      toast(ctx, `Tag "${tagName}" is already on this page`);
      return;
    }

    tag.tag(pageId);
    toast(ctx, `Tagged page with "${tagName}"`);
  };

  const removeTag = (
    ctx: SlashMenuContext,
    actionContext?: SlashMenuActionContext
  ) => {
    const tagName = getTagName(actionContext);
    if (!tagName) {
      toast(ctx, 'Type a tag name after /untag');
      return;
    }

    const pageId = ctx.std.store.id;
    const docRecord = docsService.list.doc$(pageId).value;
    if (!docRecord) {
      toast(ctx, 'Cannot untag this page');
      return;
    }

    const tag = findTagByName(tagName);
    if (!tag) {
      toast(ctx, `Tag "${tagName}" does not exist`);
      return;
    }

    const tagIds = docRecord.meta$.value.tags ?? [];
    if (!tagIds.includes(tag.id)) {
      toast(ctx, `Tag "${tagName}" is not on this page`);
      return;
    }

    tag.untag(pageId);
    toast(ctx, `Removed tag "${tagName}"`);
  };

  const applyTagById = (
    ctx: SlashMenuContext,
    tagId: string,
    name: string
  ) => {
    const pageId = ctx.std.store.id;
    const docRecord = docsService.list.doc$(pageId).value;
    if (!docRecord) {
      toast(ctx, 'Cannot tag this page');
      return;
    }
    const tag = tagService.tagList.tags$.value.find(t => t.id === tagId);
    if (!tag) {
      toast(ctx, `Tag "${name}" does not exist`);
      return;
    }
    const tagIds = docRecord.meta$.value.tags ?? [];
    if (tagIds.includes(tagId)) {
      toast(ctx, `Tag "${name}" is already on this page`);
      return;
    }
    tag.tag(pageId);
    toast(ctx, `Tagged page with "${name}"`);
  };

  const removeTagById = (
    ctx: SlashMenuContext,
    tagId: string,
    name: string
  ) => {
    const pageId = ctx.std.store.id;
    const docRecord = docsService.list.doc$(pageId).value;
    if (!docRecord) {
      toast(ctx, 'Cannot untag this page');
      return;
    }
    const tag = tagService.tagList.tags$.value.find(t => t.id === tagId);
    if (!tag) {
      toast(ctx, `Tag "${name}" does not exist`);
      return;
    }
    const tagIds = docRecord.meta$.value.tags ?? [];
    if (!tagIds.includes(tagId)) {
      toast(ctx, `Tag "${name}" is not on this page`);
      return;
    }
    tag.untag(pageId);
    toast(ctx, `Removed tag "${name}"`);
  };

  const CHIP_BORDER_COLOR = 'rgba(229, 192, 76, 0.9)';

  const applyChip = (
    ctx: SlashMenuContext,
    actionContext?: SlashMenuActionContext
  ) => {
    const chipName = getTagName(actionContext);
    if (!chipName) {
      toast(ctx, 'Type a name after /chip');
      return;
    }
    const normalized = normalizeTagName(chipName);
    const inlineEditor = getInlineEditorByModel(ctx.std, ctx.model);
    if (!inlineEditor) return;
    const inlineRange = inlineEditor.getInlineRange();
    const index = inlineRange
      ? inlineRange.index
      : (ctx.model.text?.length ?? 0);
    const chipText = `#${normalized}`;
    inlineEditor.insertText({ index, length: 0 }, chipText + ' ');
    inlineEditor.formatText(
      { index, length: chipText.length },
      {
        code: true,
        background: CHIP_BORDER_COLOR,
        color: 'var(--affine-text-primary-color)',
      }
    );
    inlineEditor.setInlineRange({
      index: index + chipText.length + 1,
      length: 0,
    });
  };

  const insertTimeLog = (ctx: SlashMenuContext) => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const text = `${hh}:${mm} - `;

    // Convert the current block to a bulleted list, then insert
    // "HH:MM - " into the new list item once its inline editor is ready.
    // We use the chain API so we can read updatedBlocks from the
    // command result, and onModelTextUpdated to wait for the rich-text
    // mount before writing into the new inline editor.
    const [success, result] = ctx.std.command
      .chain()
      .pipe(updateBlockType, {
        flavour: 'affine:list',
        props: { type: 'bulleted' },
      })
      .run();
    if (!success) return;
    const newModel = result.updatedBlocks?.[0];
    if (!newModel) return;

    onModelTextUpdated(ctx.std, newModel)
      .then(() => {
        const inlineEditor = getInlineEditorByModel(ctx.std, newModel);
        if (!inlineEditor) return;
        const existingLength = newModel.text?.length ?? 0;
        inlineEditor.insertText({ index: existingLength, length: 0 }, text);
        // updateBlockType's focusText step asynchronously sets the host
        // TextSelection back to the pre-conversion cursor index; once that
        // settles, force the cursor to the end of the inserted text.
        const targetIndex = existingLength + text.length;
        focusTextModel(ctx.std, newModel.id, targetIndex);
        queueMicrotask(() => {
          focusTextModel(ctx.std, newModel.id, targetIndex);
          inlineEditor.setInlineRange({ index: targetIndex, length: 0 });
        });
      })
      .catch(console.error);
  };

  const getChipSuggestions = (
    _ctx: SlashMenuContext,
    actionContext: SlashMenuActionContext
  ): SlashMenuActionItem[] => {
    const argument = actionContext.argument.trim();
    if (!argument) return [];
    const normalized = normalizeTagName(argument);
    return [
      {
        name: `Insert '#${normalized}'`,
        description: 'Insert as styled inline chip',
        icon: TagsIcon,
        action: (ctx: SlashMenuContext) =>
          applyChip(ctx, { ...actionContext, argument: normalized }),
      },
    ];
  };

  const filterTagsByArgument = (argument: string) => {
    const normalizedArgument = normalizeTagName(argument);
    const allTags = tagService.tagList.tags$.value;
    const filtered = normalizedArgument
      ? allTags.filter(tag =>
          normalizeTagName(tag.value$.value).includes(normalizedArgument)
        )
      : allTags;
    return filtered
      .slice()
      .sort((a, b) =>
        a.value$.value.localeCompare(b.value$.value, undefined, {
          sensitivity: 'base',
        })
      );
  };

  const getApplyTagSuggestions = (
    _ctx: SlashMenuContext,
    actionContext: SlashMenuActionContext
  ): SlashMenuActionItem[] => {
    const argument = actionContext.argument.trim();
    const suggestions = filterTagsByArgument(argument).map(tag => {
      const name = tag.value$.value;
      const color = tag.color$.value;
      const tagId = tag.id;
      return {
        name,
        description: 'Apply tag',
        icon: tagSwatchIcon(color),
        action: (ctx: SlashMenuContext) => applyTagById(ctx, tagId, name),
      } satisfies SlashMenuActionItem;
    });

    if (argument && !findTagByName(argument)) {
      suggestions.push({
        name: `Create '${argument}'`,
        description: 'Create new tag',
        icon: TagsIcon,
        action: (ctx: SlashMenuContext) =>
          applyTag(ctx, {
            ...actionContext,
            argument,
          }),
      });
    }

    return suggestions;
  };

  const getRemoveTagSuggestions = (
    ctx: SlashMenuContext,
    actionContext: SlashMenuActionContext
  ): SlashMenuActionItem[] => {
    const pageId = ctx.std.store.id;
    const docRecord = docsService.list.doc$(pageId).value;
    if (!docRecord) return [];
    const appliedTagIds = new Set(docRecord.meta$.value.tags ?? []);
    if (appliedTagIds.size === 0) return [];

    const normalizedArgument = normalizeTagName(actionContext.argument);
    const suggestions = tagService.tagList.tags$.value
      .filter(tag => appliedTagIds.has(tag.id))
      .filter(tag =>
        normalizedArgument
          ? normalizeTagName(tag.value$.value).includes(normalizedArgument)
          : true
      )
      .slice()
      .sort((a, b) =>
        a.value$.value.localeCompare(b.value$.value, undefined, {
          sensitivity: 'base',
        })
      )
      .map(tag => {
        const name = tag.value$.value;
        const color = tag.color$.value;
        const tagId = tag.id;
        return {
          name,
          description: 'Remove tag',
          icon: tagSwatchIcon(color),
          action: (ctx: SlashMenuContext) => removeTagById(ctx, tagId, name),
        } satisfies SlashMenuActionItem;
      });

    return suggestions;
  };

  return SlashMenuConfigExtension('affine-tags', {
    items: [
      {
        name: 'Tag',
        icon: TagsIcon,
        description: 'Add a tag to this page',
        group: '7_Tags@0',
        searchAlias: ['tag'],
        acceptsArgument: true,
        action: applyTag,
        getArgumentItems: getApplyTagSuggestions,
      },
      {
        name: 'Untag',
        icon: TagsIcon,
        description: 'Remove a tag from this page',
        group: '7_Tags@1',
        searchAlias: ['untag', 'removetag'],
        acceptsArgument: true,
        action: removeTag,
        getArgumentItems: getRemoveTagSuggestions,
      },
      {
        name: 'Chip',
        icon: TagsIcon,
        description: 'Insert a #chip styled inline',
        group: '7_Tags@2',
        searchAlias: ['chip', 'inlinetag', 'hashtag'],
        acceptsArgument: true,
        action: applyChip,
        getArgumentItems: getChipSuggestions,
      },
      {
        name: 'Time log',
        icon: NowIcon({ width: '20px', height: '20px' }),
        description: 'Insert a bulleted "HH:MM - " log entry',
        group: '6_Date@10',
        searchAlias: ['log', 'tlog'],
        action: insertTimeLog,
      },
    ],
  });
}
